import { Link } from 'react-router-dom'
import type { FeedGruppe, FeedZeile } from '../../hooks/feedRegeln'
import s from './FeedTafel.module.scss'

/**
 * Der Feed als kompakte Tafel.
 *
 * Drei Spalten: die Zeit, der Titel mit Art und Betrifft darunter, rechts
 * der Wert — Score, Wirkung, Stand der Idee oder die Ampel als Punkt. Die
 * Termine des Tages stehen oben in derselben Spur, mit einem Strich darunter;
 * was seit gestern Abend erschien, trägt eine Kante links. Die Tagesköpfe
 * kleben beim Scrollen unter der Kopfzeile.
 *
 * Keine Filter, keine Auswahl: Die Tafel läuft auf einem Bildschirm, den
 * niemand bedient. Wer suchen will, geht in die jeweilige Liste.
 */
export function FeedTafel({ gruppen, leer, fuss,
                            neuSeitText = 'neu seit gestern 18:00' }: {
  gruppen: FeedGruppe[]
  /** Was steht, wenn keine Zeile da ist. */
  leer: string
  /** Links unten, etwa „20 Einträge · 4 Tage". */
  fuss?: string
  /** Rechts unten hinter der Kante: seit wann etwas als neu gilt. */
  neuSeitText?: string
}) {
  const anzahl = gruppen.reduce((n, g) => n + g.termine.length + g.zeilen.length, 0)

  return (
    <div className={s.tafel}>
      {anzahl === 0 && <div className={s.leer}>{leer}</div>}
      {gruppen.map((g) => (
        <section key={g.key}>
          <h2 className={s.kopf}>
            <span>
              {g.label}
              <span className={s.kopfSub}> · {g.sub}</span>
            </span>
            <span className={s.kopfRechts}>{g.rechts}</span>
          </h2>
          {g.termine.map((z) => <Zeile key={z.key} zeile={z} />)}
          {g.termine.length > 0 && <div className={s.trenner} aria-hidden="true" />}
          {g.zeilen.map((z) => <Zeile key={z.key} zeile={z} />)}
        </section>
      ))}
      {fuss && (
        <div className={s.fuss}>
          <span>{fuss}</span>
          <span>
            <span className={s.kante} aria-hidden="true" /> {neuSeitText}
            {' · '}Zeit zeigt beim Zeigen das volle Datum
          </span>
        </div>
      )}
    </div>
  )
}

function Zeile({ zeile: z }: { zeile: FeedZeile }) {
  return (
    <div className={s.zeile} data-termin={z.termin || undefined}
         data-neu={z.neu || undefined} data-fresh={z.fresh || undefined}>
      <span className={s.zeit} title={z.zeitVoll}>{z.zeit}</span>
      <span className={s.mitte}>
        {z.to ? (
          <Link className={s.titel} to={z.to} title={z.titel}>{z.titel}</Link>
        ) : (
          <span className={s.titel} data-laedt={z.laedt || undefined}
                title={z.titel}>
            {z.titel}
          </span>
        )}
        <span className={s.sub}>
          <span className={s.art}>{z.art}</span>
          {z.betrifft && <> · {z.betrifft}</>}
        </span>
      </span>
      {z.ampel ? (
        <span className={s.ampel} title={z.wertVoll}>
          <span className={`${s.punkt} ${s[z.ampel.toLowerCase()]}`} />
        </span>
      ) : (
        <span className={s.wert} data-ton={z.wertTon}
              title={z.wertVoll || undefined}>
          {z.wert}
        </span>
      )}
    </div>
  )
}
